/**
 * Document Audit Service
 * 
 * Tracks access to documents for HIPAA compliance and audit trails.
 */

import { db } from '@healthdesk/shared-services';
import firebase from 'firebase/compat/app';

const AUDIT_COLLECTION = 'llmDocumentAccessLogs';

class DocumentAuditService {
  /**
   * Log document access
   * @param {string} documentId - Document ID
   * @param {string} userId - User ID
   * @param {string} action - Action type ('read', 'search', 'retrieve')
   * @param {Object} context - Additional context (queryId, position, etc.)
   * @returns {Promise<void>}
   */
  async logAccess(documentId, userId, action, context = {}) {
    try {
      await db.collection(AUDIT_COLLECTION).add({
        documentId,
        userId,
        action,
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        ...context,
      });
    } catch (error) {
      console.error('[DocumentAudit] Failed to log access:', error);
      // Don't throw - audit logging should not break functionality
    }
  }

  /**
   * Log document retrieval (when chunks are retrieved for RAG)
   * @param {string} documentId - Document ID
   * @param {string} userId - User ID
   * @param {string} queryId - Query ID
   * @param {number} chunkCount - Number of chunks retrieved
   * @returns {Promise<void>}
   */
  async logRetrieval(documentId, userId, queryId, chunkCount) {
    await this.logAccess(documentId, userId, 'retrieve', {
      queryId,
      chunkCount,
    });
  }

  /**
   * Get access history for a document
   * @param {string} documentId - Document ID
   * @param {number} limit - Number of records to return
   * @returns {Promise<Array>} Access log entries
   */
  async getAccessHistory(documentId, limit = 100) {
    try {
      const snapshot = await db.collection(AUDIT_COLLECTION)
        .where('documentId', '==', documentId)
        .orderBy('timestamp', 'desc')
        .limit(limit)
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || null,
      }));
    } catch (error) {
      console.error('[DocumentAudit] Failed to get access history:', error);
      return [];
    }
  }

  /**
   * Get access history for a user
   * @param {string} userId - User ID
   * @param {number} limit - Number of records to return
   * @returns {Promise<Array>} Access log entries
   */
  async getUserAccessHistory(userId, limit = 100) {
    try {
      const snapshot = await db.collection(AUDIT_COLLECTION)
        .where('userId', '==', userId)
        .orderBy('timestamp', 'desc')
        .limit(limit)
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || null,
      }));
    } catch (error) {
      console.error('[DocumentAudit] Failed to get user access history:', error);
      return [];
    }
  }
}

export const documentAuditService = new DocumentAuditService();
export default documentAuditService;
